import { useCallback, useReducer } from "react";

// TYPES

type AsyncState<Data, Error> = {
  status: "idle" | "pending" | "resolved" | "rejected";
  data?: Data | null;
  error?: Error | null;
};
type Action<Data, Error> =
  | { type: "idle" }
  | { type: "pending" }
  | { type: "resolved"; data: Data }
  | { type: "rejected"; error: Error };

// Impl

const asyncReducer = <D, E>(
  state: AsyncState<D, E>,
  action: Action<D, E>
): AsyncState<D, E> => {
  switch (action.type) {
    case "pending": {
      return { ...state, status: "pending" };
    }
    case "resolved": {
      return { status: "resolved", data: action.data, error: null };
    }
    case "rejected": {
      return { ...state, status: "rejected", error: action.error };
    }
    default: {
      return state;
    }
  }
};

// D and E in here are Data and Error on AsyncState
const useAsync = <D, E>(
  initialState: AsyncState<D, E> = { status: "idle", error: null, data: null }
) => {
  const [{ status, data, error }, dispatch] = useReducer<
    (state: AsyncState<D, E>, action: Action<D, E>) => AsyncState<D, E>
  >(asyncReducer, { ...initialState });

  const run = useCallback(
    (promise: Promise<D>, transform?: (data: any) => D) => {
      if (!promise || !promise.then) {
        throw new Error(
          `The argument passed to useAsync().run must be a promise. Maybe a function that's passed isn't returning anything?`
        );
      }
      dispatch({ type: "pending" });
      return promise.then(
        (data: D) => {
          const tData =
            typeof transform === "function" ? transform(data) : data;
          dispatch({ data: tData, type: "resolved" });
          return tData;
        },
        (error: E) => {
          dispatch({ type: "rejected", error });
          return error;
        }
      );
    },
    [dispatch]
  );

  const setData = useCallback(
    (data: D) => dispatch({ type: "resolved", data }),
    [dispatch]
  );
  const setError = useCallback(
    (error: E) => dispatch({ type: "rejected", error }),
    [dispatch]
  );
  const setLoading = useCallback(
    () => dispatch({ type: "pending" }),
    [dispatch]
  );

  return {
    isIdle: status === "idle",
    isLoading: status === "pending",
    isError: status === "rejected",
    isSuccess: status === "resolved",
    error,
    status,
    data,
    run,
    setData,
    setError,
    setLoading,
  };
};

export { useAsync };
